import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Dia local no formato YYYY-MM-DD. */
export type ISODate = string;
/** Horário local HH:MM. */
export type HHMM = string;

export const DEFAULT_TZ = 'America/Sao_Paulo';

/** Dia local (no fuso) de um instante. */
export function localDate(instant: Date, tz: string = DEFAULT_TZ): ISODate {
  return formatInTimeZone(instant, tz, 'yyyy-MM-dd');
}

/** Hora local HH:MM de um instante. */
export function localTime(instant: Date, tz: string = DEFAULT_TZ): HHMM {
  return formatInTimeZone(instant, tz, 'HH:mm');
}

/** Minutos desde a meia-noite local. */
export function localMinutes(instant: Date, tz: string = DEFAULT_TZ): number {
  return hhmmToMin(localTime(instant, tz));
}

/** Instante que corresponde a um dia e horário locais. */
export function zonedInstant(date: ISODate, time: HHMM, tz: string = DEFAULT_TZ): Date {
  return fromZonedTime(`${date}T${normTime(time)}:00`, tz);
}

/** Início do dia local seguinte (fim exclusivo do dia). */
export function endOfLocalDay(date: ISODate, tz: string = DEFAULT_TZ): Date {
  return zonedInstant(addDays(date, 1), '00:00', tz);
}

export function normTime(t: string): HHMM {
  return t.slice(0, 5);
}

export function hhmmToMin(t: string): number {
  const [h, m] = normTime(t).split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function minToHhmm(min: number): HHMM {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function addMinutesHhmm(t: string, delta: number): HHMM {
  return minToHhmm(hhmmToMin(t) + delta);
}

function toUTC(d: ISODate): Date {
  return new Date(`${d}T00:00:00Z`);
}

export function addDays(d: ISODate, n: number): ISODate {
  const x = toUTC(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

/** a − b em dias. */
export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((toUTC(a).getTime() - toUTC(b).getTime()) / 86_400_000);
}

/** Dia da semana ISO: 1 = segunda … 7 = domingo. */
export function isodow(d: ISODate): number {
  const w = toUTC(d).getUTCDay();
  return w === 0 ? 7 : w;
}

/** Segunda-feira da semana do calendário (segunda a domingo) que contém `d`. */
export function mondayOf(d: ISODate): ISODate {
  return addDays(d, -(isodow(d) - 1));
}

export function dateRange(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function monthOf(d: ISODate): string {
  return d.slice(0, 7);
}
